import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

// ── Read user synchronously from localStorage on first load ──
// This prevents the null flash that causes wrong redirects
function getUserFromStorage() {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return null;

    // Parse JWT payload to get user info instantly (no API call needed)
    const payload = JSON.parse(atob(token.split('.')[1]));

    // Check token not expired
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      localStorage.clear();
      return null;
    }

    return {
      username:  payload.username  || '',
      role:      payload.role      || '',
      full_name: payload.full_name || '',
      email:     payload.email     || '',
      van_no:    payload.van_no    || '',
    };
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  // ── Initialize user SYNCHRONOUSLY from token — no null flash ──
  const [user,    setUser]    = useState(() => getUserFromStorage());
  const [loading, setLoading] = useState(false); // no async wait needed

  // ── Optional: silently verify token with backend in background ──
  // Does NOT block rendering or cause redirects
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token || !user) return;

    authService.getMe()
      .then(data => {
        // Update with fresh server data silently
        setUser(prev => prev ? { ...prev, ...data } : null);
      })
      .catch(() => {
        // Token invalid — clear and logout silently
        localStorage.clear();
        sessionStorage.clear(); // ← clear session data too
        setUser(null);
      });
  }, []); // runs once on mount, never blocks navigation

  const login = async (username, password) => {
    const data = await authService.login(username, password);
    localStorage.setItem('access_token',  data.access);
    localStorage.setItem('refresh_token', data.refresh);
    const newUser = {
      username:  data.username,
      role:      data.role,
      full_name: data.full_name,
      email:     data.email,
      van_no:    data.van_no  || '',
    };
    setUser(newUser);
    return data;
  };

  const logout = () => {
    localStorage.clear();
    sessionStorage.clear(); // ← ADDED: clear patient session data on logout
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);